'use client'

import { initiateIRacingOAuth } from '@/actions/iracing/initiate-oauth'
import ApplicationDialog from '@/components/_registration/application/application-dialog'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDurationCountdown } from '@/lib/countdown'
import {
  canRecruitForChampionship,
  getEffectiveRecruitmentOpenDateTime,
} from '@/lib/recruitment-scheduling'
import { cn } from '@/lib/utils'
import { useRouter } from 'next/navigation'
import {
  useActionState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useFormStatus } from 'react-dom'
import toast from 'react-hot-toast'
import {
  getChampionshipApplyDataAction,
  submitChampionshipApplicationAction,
  withdrawChampionshipApplicationAction,
} from '../actions/apply'

function useRegistrationCountdown(registrationOpenDateTime) {
  const [now, setNow] = useState(null)
  const registrationOpenTimestamp = registrationOpenDateTime
    ? new Date(registrationOpenDateTime).getTime()
    : null
  const hasValidRegistrationOpenTimestamp = Number.isFinite(
    registrationOpenTimestamp,
  )

  useEffect(() => {
    if (!hasValidRegistrationOpenTimestamp) return

    const currentNow = Date.now()
    setNow(currentNow)

    if (registrationOpenTimestamp <= currentNow) return

    const intervalId = window.setInterval(() => {
      const nextNow = Date.now()
      setNow(nextNow)

      if (registrationOpenTimestamp <= nextNow) {
        window.clearInterval(intervalId)
      }
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [hasValidRegistrationOpenTimestamp, registrationOpenTimestamp])

  if (!hasValidRegistrationOpenTimestamp || !now) {
    return {
      isScheduled: false,
      label: null,
    }
  }

  if (registrationOpenTimestamp <= now) {
    return {
      isScheduled: false,
      label: null,
    }
  }

  return {
    isScheduled: true,
    label: formatDurationCountdown(registrationOpenTimestamp - now),
  }
}

export default function ChampionshipAttendeeStatusButton({
  session,
  championship,
  event = null,
  presetAttendee = null,
  skipInitialFetch = false,
  mutatePresetAttendee = null,
  mutateDriverCount = null,
  increaseDriverCount,
  initialUserIsWhitelisted = true,
  onRegistrationSuccess = null,
  registerLabel = 'Register',
}) {
  const router = useRouter()
  const [accountLinkingModalOpen, setAccountLinkingModalOpen] = useState(false)
  const [applicationModalOpen, setApplicationModalOpen] = useState(false)
  const [hasLoadedApplyData, setHasLoadedApplyData] = useState(false)
  const [linkingRequiredAccount, setLinkingRequiredAccount] = useState(false)
  const [loadingApplyData, setLoadingApplyData] = useState(false)
  const [applyData, setApplyData] = useState({
    attendee: presetAttendee,
    userIsWhitelisted: initialUserIsWhitelisted,
    applicationQuestions: [],
    isApplicationFormEnabled: true,
    hasRequiredAccountLinked: true,
    requiredAccountLinkingService: null,
    futureEvents: [],
    carClasses: null,
    user: null,
    prefilledApplicationAnswers: {},
    applicationTransfer: null,
  })
  const [submitState, submitFormAction] = useActionState(
    submitChampionshipApplicationAction,
    null,
  )
  const lastHandledSubmitStateRef = useRef(null)
  const registrationOpenDateTime = getEffectiveRecruitmentOpenDateTime(
    championship,
    event,
  )
  const registrationCountdown = useRegistrationCountdown(
    registrationOpenDateTime,
  )
  const registrationIsOpen = canRecruitForChampionship(championship)
  const registrationIsScheduled =
    !registrationIsOpen && registrationCountdown.isScheduled

  const sessionUserId = session?.user?.id || null

  const loadApplyData = useCallback(async () => {
    if (!sessionUserId) return

    setLoadingApplyData(true)

    try {
      const response = await getChampionshipApplyDataAction({
        championshipId: championship.id,
      })

      if (!response?.ok || !response.data) {
        throw new Error(response?.message || 'Failed to load application data')
      }

      const nextApplyData = {
        attendee:
          response.data.attendee ||
          (presetAttendee
            ? {
                ...presetAttendee,
                userId:
                  presetAttendee.userId ||
                  presetAttendee.user?.id ||
                  sessionUserId,
              }
            : null),
        userIsWhitelisted: response.data.userIsWhitelisted,
        applicationQuestions: response.data.applicationQuestions || [],
        isApplicationFormEnabled:
          response.data.isApplicationFormEnabled ?? true,
        hasRequiredAccountLinked:
          response.data.hasRequiredAccountLinked ?? true,
        requiredAccountLinkingService:
          response.data.requiredAccountLinkingService || null,
        futureEvents: response.data.futureEvents || [],
        user: response.data.user || null,
        carClasses: response.data.carClasses || null,
        prefilledApplicationAnswers:
          response.data.prefilledApplicationAnswers || {},
        applicationTransfer: response.data.applicationTransfer || null,
      }

      setApplyData(nextApplyData)
      setHasLoadedApplyData(true)

      return nextApplyData
    } catch (error) {
      const message = error.message || 'Failed to load apply flow'
      if (message !== 'Championship registration is not open.') {
        toast.error(message)
      }
      return null
    } finally {
      setLoadingApplyData(false)
    }
  }, [championship.id, presetAttendee, sessionUserId])

  useEffect(() => {
    if (!sessionUserId) return
    if (!registrationIsOpen) return
    if (skipInitialFetch && !applicationModalOpen) return
    void loadApplyData()
  }, [
    loadApplyData,
    sessionUserId,
    applicationModalOpen,
    skipInitialFetch,
    registrationIsOpen,
  ])

  useEffect(() => {
    if (!submitState) return
    if (lastHandledSubmitStateRef.current === submitState) return

    lastHandledSubmitStateRef.current = submitState

    if (!submitState.ok) {
      toast.error(submitState.message || 'Failed to submit application', {
        id: 'championship-apply-submit',
      })
      return
    }

    const becameAccepted =
      submitState.attendee?.status === 'accepted' &&
      applyData.attendee?.status !== 'accepted'

    if (becameAccepted && increaseDriverCount) {
      increaseDriverCount()
    }

    toast.success(
      submitState.message ||
        (submitState.attendee?.status === 'pending'
          ? 'Application pending!'
          : 'Joined championship!'),
      {
        id: 'championship-apply-submit',
      },
    )

    setApplicationModalOpen(false)
    setAccountLinkingModalOpen(false)
    onRegistrationSuccess?.({
      status: submitState.attendee?.status || null,
    })

    void (async () => {
      await loadApplyData()

      if (mutatePresetAttendee) {
        await mutatePresetAttendee()
      }

      if (mutateDriverCount) {
        await mutateDriverCount()
      }

      router.refresh()
    })()
  }, [
    applyData.attendee?.status,
    increaseDriverCount,
    loadApplyData,
    mutateDriverCount,
    mutatePresetAttendee,
    onRegistrationSuccess,
    router,
    submitState,
  ])

  const resolvedChampionshipAttendee = useMemo(() => {
    if (!applyData.attendee) return null

    return {
      ...applyData.attendee,
      userId:
        applyData.attendee.userId ||
        applyData.attendee.user?.id ||
        sessionUserId ||
        null,
    }
  }, [applyData.attendee, sessionUserId])

  const handleRegisterClick = useCallback(async () => {
    if (loadingApplyData) return
    if (!registrationIsOpen) return

    const latestApplyData = hasLoadedApplyData
      ? applyData
      : await loadApplyData()

    if (!latestApplyData) return

    if (
      latestApplyData.requiredAccountLinkingService &&
      !latestApplyData.hasRequiredAccountLinked
    ) {
      setAccountLinkingModalOpen(true)
      return
    }

    setApplicationModalOpen(true)
  }, [
    applyData,
    hasLoadedApplyData,
    loadApplyData,
    loadingApplyData,
    registrationIsOpen,
  ])

  const handleRequiredAccountLink = useCallback(async () => {
    const popup = window.open('', '_blank')

    if (popup) {
      popup.opener = null
    }

    try {
      setLinkingRequiredAccount(true)
      const url = await initiateIRacingOAuth()

      if (popup && !popup.closed) {
        popup.location.replace(url)
      } else {
        window.location.assign(url)
      }
    } catch (error) {
      if (popup && !popup.closed) {
        popup.close()
      }

      toast.error(error.message || 'Failed to start iRacing account linking')
    } finally {
      setLinkingRequiredAccount(false)
    }
  }, [])

  const handleAccountLinkedRetry = useCallback(async () => {
    const latestApplyData = await loadApplyData()

    if (!latestApplyData) return

    if (
      latestApplyData.requiredAccountLinkingService &&
      !latestApplyData.hasRequiredAccountLinked
    ) {
      toast.error('Your iRacing account is not linked yet.', {
        id: 'championship-required-account-check',
      })
      return
    }

    setAccountLinkingModalOpen(false)
    setApplicationModalOpen(true)
  }, [loadApplyData])

  if (loadingApplyData && !hasLoadedApplyData) {
    return <Skeleton className="h-full w-full rounded-sm" />
  }

  if (
    !resolvedChampionshipAttendee ||
    resolvedChampionshipAttendee.status !== 'accepted'
  ) {
    return (
      <>
        <RequiredAccountLinkingDialog
          open={accountLinkingModalOpen}
          linking={linkingRequiredAccount}
          onLinkAccount={handleRequiredAccountLink}
          onCheckAgain={handleAccountLinkedRetry}
          onOpenChange={setAccountLinkingModalOpen}
        />

        <ApplicationDialog
          championshipId={championship.id}
          championship={championship}
          applicationQuestions={applyData.applicationQuestions}
          isApplicationFormEnabled={applyData.isApplicationFormEnabled}
          futureEvents={applyData.futureEvents}
          formAction={submitFormAction}
          open={applicationModalOpen}
          setOpen={setApplicationModalOpen}
          carClasses={applyData.carClasses ?? championship.carClasses}
          user={applyData.user || session?.user}
          platforms={championship.platforms}
          initialApplicationAnswers={applyData.prefilledApplicationAnswers}
          applicationTransfer={applyData.applicationTransfer}
        />

        <Button
          type="button"
          variant={registrationIsOpen ? 'default' : 'outline-solid'}
          className={cn('z-20 h-full min-h-[44px] w-full lg:z-0', {
            'text-white!': !registrationIsScheduled,
            'bg-linear-to-b hover:from-primary-600! hover:to-primary-600! border-none from-primary-500 to-[#FF3D00]':
              registrationIsOpen,
            'bg-neutral-blue-800! text-neutral-blue-100! cursor-not-allowed border-neutral-blue-600 px-3 text-xs font-semibold disabled:opacity-100':
              registrationIsScheduled,
          })}
          disabled={
            (resolvedChampionshipAttendee?.status === 'pending' &&
              applyData.isApplicationFormEnabled) ||
            !registrationIsOpen ||
            (championship.isWhitelistOnly && !applyData.userIsWhitelisted)
          }
          data-type="championship"
          onClick={() => void handleRegisterClick()}
        >
          {championship.isWhitelistOnly && !applyData.userIsWhitelisted
            ? 'Not Whitelisted'
            : registrationIsScheduled
              ? `Championship registration opens in ${registrationCountdown.label}`
              : !championship.isRecruiting
                ? 'Not Available'
                : resolvedChampionshipAttendee?.status === 'pending' &&
                    applyData.isApplicationFormEnabled
                  ? 'Application Pending'
                  : registerLabel}
        </Button>
      </>
    )
  }

  return (
    <ConfirmWithdrawDialog
      championshipId={championship.id}
      onWithdrawn={async () => {
        await loadApplyData()

        if (mutatePresetAttendee) {
          await mutatePresetAttendee()
        }

        if (mutateDriverCount) {
          await mutateDriverCount()
        }

        router.refresh()
      }}
    />
  )
}

function RequiredAccountLinkingDialog({
  open,
  linking,
  onCheckAgain,
  onLinkAccount,
  onOpenChange,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="px-2 py-3 md:p-5">
        <DialogHeader>
          <DialogTitle>Link iRacing Account</DialogTitle>
          <DialogDescription>
            This championship requires a linked iRacing account before you can
            apply. Link it in a new tab, then come back here and continue.
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="min-w-1/2 grow"
            onClick={onCheckAgain}
          >
            I&apos;ve linked it
          </Button>
          <Button
            type="button"
            className="min-w-1/2 grow"
            disabled={linking}
            onClick={onLinkAccount}
          >
            {linking ? 'Opening iRacing...' : 'Link iRacing account'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ConfirmWithdrawDialog({ championshipId, onWithdrawn }) {
  const [open, setOpen] = useState(false)
  const [withdrawState, withdrawFormAction] = useActionState(
    withdrawChampionshipApplicationAction,
    null,
  )
  const lastHandledWithdrawStateRef = useRef(null)

  useEffect(() => {
    if (!withdrawState) return
    if (lastHandledWithdrawStateRef.current === withdrawState) return

    lastHandledWithdrawStateRef.current = withdrawState

    if (!withdrawState.ok) {
      toast.error(withdrawState.message || 'Failed to withdraw', {
        id: 'championship-withdraw-submit',
      })
      return
    }

    toast.success(
      withdrawState.message || 'Successfully withdrew application.',
      {
        id: 'championship-withdraw-submit',
      },
    )
    setOpen(false)
    void onWithdrawn()
  }, [onWithdrawn, withdrawState])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="border-white! z-20 h-[57px] w-full lg:z-0"
        >
          Withdraw
        </Button>
      </DialogTrigger>
      <DialogContent className="px-2 py-3 md:p-5">
        <DialogHeader>
          <DialogTitle>Confirm Leaving Championship</DialogTitle>
          <DialogDescription>
            This will also remove you from all events in this championship.
          </DialogDescription>
        </DialogHeader>

        <form action={withdrawFormAction}>
          <input type="hidden" name="championshipId" value={championshipId} />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="min-w-1/2 grow"
              onClick={() => setOpen(false)}
            >
              Close
            </Button>
            <WithdrawSubmitButton />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function WithdrawSubmitButton() {
  const { pending } = useFormStatus()

  return (
    <Button
      type="submit"
      variant="destructive"
      className="min-w-1/2 grow"
      disabled={pending}
    >
      {pending ? 'Withdrawing...' : 'Yes, leave championship'}
    </Button>
  )
}
